import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Close,
  Edit,
  Save,
  Send,
  Print,
} from '@mui/icons-material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import DocumentHeader from '../documents/DocumentHeader';
import DocumentFooter from '../documents/DocumentFooter';
import LegalClausesSection from '../documents/LegalClausesSection';
import {
  renderMarkdown,
  SectionWrapper,
  SectionHeader,
  FieldLabel,
  DOCUMENT_COLORS,
  APPBAR_GRADIENT,
} from '../documents/documentStyles';

/**
 * MilestoneReportDocumentPreview Component
 *
 * A professional, document-style preview of the milestone report.
 * Allows final review and edits before submission.
 */
const MilestoneReportDocumentPreview = ({
  open,
  onClose,
  onSubmit,
  data,
  contractor,
  onEditSection,
  loading
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.currency || 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartEdit = (section, value) => {
    setEditingSection(section);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    onEditSection(editingSection, editValue);
    setEditingSection(null);
    setEditValue('');
  };

  const handleSubmit = () => {
    const errors = {};
    if ((data.scopeOfWork || '').trim().length < 50) {
      errors.scopeOfWork = 'Scope of Work must be at least 50 characters';
    }
    if ((data.milestoneOverview || '').trim().length < 50) {
      errors.milestoneOverview = 'Milestone Overview must be at least 50 characters';
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    onSubmit();
  };

  const isSubmittable =
    (data.scopeOfWork || '').trim().length >= 50 &&
    (data.milestoneOverview || '').trim().length >= 50;

  const totalCost =
    parseFloat(data.laborCost || 0) +
    parseFloat(data.materialsCost || 0) +
    (data.otherCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);

  const hasMilestoneOverview = !!data.milestoneOverview;
  const hasExclusions = data.exclusions && data.exclusions.length > 0;
  const hasAcceptanceCriteria = !!data.acceptanceCriteria;
  const hasReferencePhotos = data.referencePhotos && data.referencePhotos.length > 0;
  const hasReferenceDocuments = data.referenceDocuments && data.referenceDocuments.length > 0;
  const hasPaymentTerms = !!data.paymentTerms;
  const isFinalHandover = data.reportMode === 'final_handover';
  const finalHandover = data.finalHandover || {};
  const hasFinalHandover =
    isFinalHandover &&
    (
      finalHandover.summary ||
      finalHandover.certificates?.length > 0 ||
      finalHandover.punchListItems?.length > 0 ||
      finalHandover.contractorSignoff
    );

  let _sec = 0;
  const nextSec = () => ++_sec;
  const sec = {
    milestoneOverview: hasMilestoneOverview ? nextSec() : null,
    scopeOfWork: nextSec(),
    deliverablesMatrix: nextSec(),
    exclusions: hasExclusions ? nextSec() : null,
    acceptanceCriteria: hasAcceptanceCriteria ? nextSec() : null,
    referencePhotos: hasReferencePhotos ? nextSec() : null,
    referenceDocuments: hasReferenceDocuments ? nextSec() : null,
    costSummary: nextSec(),
    paymentTerms: hasPaymentTerms ? nextSec() : null,
    finalHandover: hasFinalHandover ? nextSec() : null,
    legalTerms: nextSec(),
    approvalProcess: nextSec(),
  };

  const renderEditableText = (section, value, multiline = false) => {
    if (editingSection === section) {
      return (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline={multiline}
            rows={multiline ? 4 : 1}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
            sx={{ mb: 1, bgcolor: 'background.paper' }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setEditingSection(null)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSaveEdit} startIcon={<Save />}>
              Save
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover .edit-icon': { opacity: 1 },
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={() => handleStartEdit(section, value)}
      >
        {multiline && value ? (
          renderMarkdown(value)
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {value || 'Click to add content...'}
          </Typography>
        )}
        <IconButton
          className="edit-icon"
          size="small"
          sx={{
            position: 'absolute', top: 0, right: 0,
            opacity: 0, transition: 'opacity 0.2s',
            bgcolor: 'background.paper'
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const submissionDate = formatDate(data.submitted_at || data.created_at);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative', background: APPBAR_GRADIENT }} elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, fontWeight: 600 }} variant="h6" component="div">
            {isFinalHandover ? 'Final Handover Report Preview & Finalization' : 'Stage Completion Report Preview & Finalization'}
          </Typography>
          <Button color="inherit" onClick={() => window.print()} sx={{ mr: 1, textTransform: 'none' }}>
            <Print sx={{ mr: 1 }} /> Print
          </Button>
          <Button
            autoFocus
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? null : <Send />}
            sx={{
              background: '#fff',
              color: DOCUMENT_COLORS.brand,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { background: '#F0F4FF' }
            }}
          >
            {loading ? 'Submitting...' : 'Submit to Client'}
          </Button>
        </Toolbar>
        {!isSubmittable && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Before you can submit: the Stage Overview and Scope of Work must each be at least 50 characters
            {(data.milestoneOverview || '').trim().length < 50 ? ` (overview is ${(data.milestoneOverview || '').trim().length})` : ''}
            {(data.scopeOfWork || '').trim().length < 50 ? ` (scope is ${(data.scopeOfWork || '').trim().length})` : ''}.
            Tap the pencil on either section to add detail.
          </Alert>
        )}
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4, bgcolor: DOCUMENT_COLORS.dialogBg, minHeight: '100vh' }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, mb: 4, borderRadius: 2 }} id="milestone-report-document">

          {/* Document Header */}
          <DocumentHeader
            documentType={isFinalHandover ? 'FINAL HANDOVER REPORT' : 'STAGE COMPLETION REPORT'}
            issuer={{
              name: contractor?.companyName || contractor?.name,
              logoUrl: contractor?.logo || contractor?.profile_image,
              city: contractor?.city || 'Zimbabwe',
            }}
            date={submissionDate !== 'TBD' ? submissionDate : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            projectTitle={data.milestoneName}
          />

          <Divider sx={{ mb: 4 }} />

          {/* Executive Summary */}
          <Box sx={{ mb: 4, p: 3, bgcolor: DOCUMENT_COLORS.brandLight, borderRadius: 2, borderLeft: `5px solid ${DOCUMENT_COLORS.brand}` }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: DOCUMENT_COLORS.brandDark }}>
              Executive Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="body1" paragraph>
                  This {isFinalHandover ? 'final handover report' : 'stage completion report'} outlines the scope, deliverables, costs, and timeline for <strong>{data.milestoneName}</strong>.
                  {data.estimatedDuration && ` The work is estimated to take ${data.estimatedDuration} ${data.durationUnit}.`}
                  {data.deliverables && data.deliverables.length > 0 && ` This stage includes ${data.deliverables.length} key deliverables.`}
                </Typography>
                {data.milestoneOverview && (
                  <Typography variant="body2" color="text.secondary">
                    {data.milestoneOverview.substring(0, 200)}...
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <FieldLabel>Key Metrics</FieldLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                      <Typography variant="h6" sx={{ color: DOCUMENT_COLORS.brand }}>{formatCurrency(totalCost)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">{data.estimatedDuration || 'TBD'} {data.durationUnit || ''}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Deliverables</Typography>
                      <Typography variant="body1">{data.deliverables?.length || 0} items</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* 1. Stage Overview */}
          {hasMilestoneOverview && (
            <SectionWrapper>
              <SectionHeader
                icon={<FlagOutlinedIcon fontSize="small" />}
                title={`${sec.milestoneOverview}. Stage Overview & Objectives`}
              />
              {validationErrors.milestoneOverview && (
                <Alert severity="error" sx={{ mb: 1 }}>{validationErrors.milestoneOverview}</Alert>
              )}
              <Box sx={{
                border: validationErrors.milestoneOverview ? '2px solid #d32f2f' : 'none',
                borderRadius: 1,
                p: validationErrors.milestoneOverview ? 1 : 0,
              }}>
                {renderEditableText('milestoneOverview', data.milestoneOverview, true)}
              </Box>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Key Objectives:
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary={"\u2713 Complete all specified deliverables to quality standards"} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary={"\u2713 Meet all acceptance criteria and pass inspections"} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary={"\u2713 Ensure compliance with safety regulations"} />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary={"\u2713 Maintain project timeline and budget"} />
                </ListItem>
              </List>
            </SectionWrapper>
          )}

          {/* Scope of Work */}
          <SectionWrapper>
            <SectionHeader
              icon={<BuildOutlinedIcon fontSize="small" />}
              title={`${sec.scopeOfWork}. Scope of Work`}
            />
            {validationErrors.scopeOfWork && (
              <Alert severity="error" sx={{ mb: 1 }}>{validationErrors.scopeOfWork}</Alert>
            )}
            <Box sx={{
              border: validationErrors.scopeOfWork ? '2px solid #d32f2f' : 'none',
              borderRadius: 1,
              p: validationErrors.scopeOfWork ? 1 : 0,
            }}>
              {renderEditableText('scopeOfWork', data.scopeOfWork, true)}
            </Box>
          </SectionWrapper>

          {/* Deliverables Matrix */}
          <SectionWrapper>
            <SectionHeader
              icon={<ViewListOutlinedIcon fontSize="small" />}
              title={`${sec.deliverablesMatrix}. Deliverables Matrix`}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The following deliverables will be provided upon stage completion. Each deliverable is subject to inspection and must meet specified acceptance criteria and quality standards.
            </Typography>
            {data.deliverables && data.deliverables.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: DOCUMENT_COLORS.brand }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '5%', color: 'white' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '25%', color: 'white' }}>Deliverable Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '12%', color: 'white' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '28%', color: 'white' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '30%', color: 'white' }}>Acceptance Criteria & Quality Standards</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.deliverables.map((deliverable, index) => (
                        <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: DOCUMENT_COLORS.brand }}>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                              {deliverable.name}
                            </Typography>
                            {deliverable.aiSuggested && (
                              <Chip label="AI Generated" size="small" color="secondary" sx={{ fontSize: '0.65rem', height: 18 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={deliverable.type || 'physical'}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize', fontWeight: 'bold', borderColor: DOCUMENT_COLORS.brand, color: DOCUMENT_COLORS.brand }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{deliverable.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ bgcolor: 'success.50', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
                              <Typography variant="caption" fontWeight="bold" display="block" color="success.dark" gutterBottom>
                                ACCEPTANCE CRITERIA:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {"\u2713"} {deliverable.acceptanceCriteria}
                              </Typography>
                              {deliverable.qualityStandard && (
                                <>
                                  <Typography variant="caption" fontWeight="bold" display="block" color="info.dark" sx={{ mt: 1 }} gutterBottom>
                                    QUALITY STANDARD:
                                  </Typography>
                                  <Chip label={deliverable.qualityStandard} size="small" sx={{ fontSize: '0.7rem', height: 20 }} color="info" />
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Deliverables Summary Stats */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: DOCUMENT_COLORS.brandLight }}>
                      <Typography variant="h4" sx={{ color: DOCUMENT_COLORS.brand }}>{data.deliverables.length}</Typography>
                      <Typography variant="caption">Total Deliverables</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                      <Typography variant="h4" color="success.main">
                        {data.deliverables.filter(d => d.qualityStandard).length}
                      </Typography>
                      <Typography variant="caption">With Quality Standards</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="h4" color="text.secondary">
                        {data.deliverables.filter(d => d.type === 'physical').length}
                      </Typography>
                      <Typography variant="caption">Physical Outputs</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                      <Typography variant="h4" color="warning.dark">
                        {data.deliverables.filter(d => d.type === 'inspection').length}
                      </Typography>
                      <Typography variant="caption">Inspections Required</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Alert severity="warning">No deliverables specified</Alert>
            )}

            {/* Quality Assurance Process */}
            <Paper variant="outlined" sx={{ p: 3, bgcolor: DOCUMENT_COLORS.brandLight, borderColor: DOCUMENT_COLORS.brand, borderLeft: `5px solid ${DOCUMENT_COLORS.brand}` }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: DOCUMENT_COLORS.brandDark }}>
                Quality Assurance & Inspection Process
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" paragraph>
                    <strong>Verification Requirements:</strong>
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary={"\u2022 All deliverables must meet 100% of acceptance criteria"} />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary={"\u2022 Quality standards compliance is mandatory"} />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary={"\u2022 Independent inspection may be required"} />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary={"\u2022 Photographic evidence required for completion"} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" paragraph>
                    <strong>Non-Compliance:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Any deliverable not meeting specified quality standards or acceptance criteria will require rework at the contractor's expense. Payment will only be released upon successful verification and approval of all deliverables.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </SectionWrapper>

          {/* Exclusions */}
          {hasExclusions && (
            <SectionWrapper>
              <SectionHeader
                icon={<BlockOutlinedIcon fontSize="small" />}
                title={`${sec.exclusions}. Exclusions`}
              />
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50', borderColor: 'error.light' }}>
                <Typography variant="subtitle2" color="error.main" fontWeight="bold" gutterBottom>
                  NOT INCLUDED IN THIS STAGE
                </Typography>
                <List dense disablePadding>
                  {data.exclusions.map((item, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 0.25 }}>
                      <ListItemText primary={`\u2022 ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </SectionWrapper>
          )}

          {/* Acceptance Criteria */}
          {hasAcceptanceCriteria && (
            <SectionWrapper>
              <SectionHeader
                icon={<VerifiedOutlinedIcon fontSize="small" />}
                title={`${sec.acceptanceCriteria}. Acceptance Criteria`}
              />
              {renderEditableText('acceptanceCriteria', data.acceptanceCriteria, true)}
            </SectionWrapper>
          )}

          {/* Reference Photos */}
          {hasReferencePhotos && (
            <SectionWrapper>
              <SectionHeader
                icon={<PhotoLibraryOutlinedIcon fontSize="small" />}
                title={`${sec.referencePhotos}. Reference Photos`}
              />
              <Grid container spacing={2}>
                {data.referencePhotos.map((photo, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Box
                        component="img"
                        src={photo.url}
                        sx={{ width: '100%', height: 150, objectFit: 'cover' }}
                      />
                      {photo.caption && (
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" color="text.secondary">
                            {photo.caption}
                          </Typography>
                        </CardContent>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </SectionWrapper>
          )}

          {/* Reference Documents */}
          {hasReferenceDocuments && (
            <SectionWrapper>
              <SectionHeader
                icon={<DescriptionOutlinedIcon fontSize="small" />}
                title={`${sec.referenceDocuments}. Completion Documents`}
              />
              <List dense disablePadding>
                {data.referenceDocuments.map((document, index) => (
                  <ListItem key={`${document.name || 'document'}-${index}`} sx={{ px: 0, py: 0.75 }}>
                    <ListItemText
                      primary={document.url
                        ? <a href={document.url} target="_blank" rel="noopener noreferrer">{document.name || `Document ${index + 1}`}</a>
                        : (document.name || `Document ${index + 1}`)}
                      secondary={document.note || document.type || 'Uploaded document'}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </SectionWrapper>
          )}

          {/* Cost Summary & Timeline */}
          <SectionWrapper>
            <SectionHeader
              icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
              title={`${sec.costSummary}. Cost Summary & Timeline`}
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: DOCUMENT_COLORS.brand }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Description</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.laborCost && parseFloat(data.laborCost) > 0 && (
                        <TableRow>
                          <TableCell>Labor Cost</TableCell>
                          <TableCell align="right">{formatCurrency(data.laborCost)}</TableCell>
                        </TableRow>
                      )}
                      {data.materialsCost && parseFloat(data.materialsCost) > 0 && (
                        <TableRow>
                          <TableCell>Materials Cost</TableCell>
                          <TableCell align="right">{formatCurrency(data.materialsCost)}</TableCell>
                        </TableRow>
                      )}
                      {data.otherCharges && data.otherCharges.map((charge, index) => (
                        <TableRow key={index}>
                          <TableCell>{charge.description}</TableCell>
                          <TableCell align="right">{formatCurrency(charge.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: DOCUMENT_COLORS.brandLight }}>
                        <TableCell><strong>TOTAL STAGE COST</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(totalCost)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {data.materialCostCoverage && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: DOCUMENT_COLORS.brandLight, borderColor: DOCUMENT_COLORS.brand }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Material Cost Coverage
                    </Typography>
                    <Typography variant="body2">
                      {data.materialCostCoverage === 'contractor' ? 'Contractor covers materials' :
                       data.materialCostCoverage === 'client' ? 'Client covers materials' :
                       'Shared coverage'}
                    </Typography>
                    {data.materialCostCoverageDetails && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {data.materialCostCoverageDetails}
                      </Typography>
                    )}
                  </Paper>
                )}
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <FieldLabel>Project Timeline</FieldLabel>
                  {data.estimatedDuration && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Estimated Duration:</Typography>
                      <Typography variant="h6">{data.estimatedDuration} {data.durationUnit}</Typography>
                    </Box>
                  )}
                  {data.dependencies && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Dependencies:</Typography>
                      <Typography variant="body2">{data.dependencies}</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* Payment Terms */}
          {hasPaymentTerms && (
            <SectionWrapper>
              <SectionHeader
                icon={<PaymentsOutlinedIcon fontSize="small" />}
                title={`${sec.paymentTerms}. Payment Terms & Schedule`}
              />
              <Paper variant="outlined" sx={{ p: 3, borderLeft: '5px solid', borderColor: 'success.main' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {data.paymentTerms}
                </Typography>
              </Paper>
            </SectionWrapper>
          )}

          {hasFinalHandover && (
            <SectionWrapper>
              <SectionHeader
                icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}
                title={`${sec.finalHandover}. Final Handover`}
              />

              {finalHandover.summary && (
                <Paper variant="outlined" sx={{ p: 3, mb: 2, borderLeft: '5px solid', borderColor: DOCUMENT_COLORS.brand }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Handover Summary
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {finalHandover.summary}
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Certificates, Warranties & Documents
                    </Typography>
                    {finalHandover.certificates?.length > 0 ? (
                      <List dense disablePadding>
                        {finalHandover.certificates.map((item, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText primary={`\u2713 ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No handover documents listed.</Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Punch List / Snag Items
                    </Typography>
                    {finalHandover.punchListItems?.length > 0 ? (
                      <List dense disablePadding>
                        {finalHandover.punchListItems.map((item, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={`${item.resolved ? '\u2713' : '\u25cb'} ${item.text || item}`}
                              secondary={item.resolved ? 'Resolved' : 'Open'}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No punch-list items listed.</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Alert severity={finalHandover.contractorSignoff ? 'success' : 'warning'} sx={{ mt: 2 }}>
                {finalHandover.contractorSignoff
                  ? 'Contractor has signed off that the work is complete and ready for owner review.'
                  : 'Contractor sign-off has not been confirmed.'}
              </Alert>
            </SectionWrapper>
          )}

          {/* Legal Terms & Conditions */}
          <LegalClausesSection
            documentType="milestone_report"
            sectionNumber={sec.legalTerms}
            additionalTerms={data.paymentTerms}
          />

          {/* Approval Process & Sign-off */}
          <SectionWrapper>
            <SectionHeader
              icon={<HowToRegOutlinedIcon fontSize="small" />}
              title={`${sec.approvalProcess}. Approval Process & Sign-off`}
            />

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Approval Process
              </Typography>
              <Typography variant="body2">
                Upon submission, the client will review this {isFinalHandover ? 'final handover report' : 'stage completion report'} and either approve, request changes, or reject.
                Once approved, the stage will be recorded in the project timeline and payment will be released per the agreed terms.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Contractor Sign-off
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {contractor?.companyName || contractor?.name || 'Contractor'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submitted: {submissionDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2, minHeight: 120, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Client Approval
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Will be recorded upon approval
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </SectionWrapper>

          <DocumentFooter documentType={isFinalHandover ? 'final handover report' : 'stage completion report'} />

        </Paper>
      </Container>
    </Dialog>
  );
};

export default MilestoneReportDocumentPreview;
